/**
 * TasksTab — CRM follow-up tasks with open/done partitioning (iter 170 split).
 *
 * TaskRow is intentionally local — nothing else in the codebase renders
 * a CRM task row, so making it a separate module would be premature.
 */
import React, { useState } from 'react';
import { Plus, CheckSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { relTime } from './formatters';

function TaskRow({ task, onPatch, busy }) {
  const done = task.status !== 'open';
  return (
    <div
      className={`rounded-lg border p-3 mb-2 flex items-center gap-3 ${
        done
          ? 'border-slate-800 bg-slate-900/20 opacity-60'
          : 'border-slate-800 bg-slate-900/40'
      }`}
      data-testid={`crm-task-${task.id}`}
    >
      <button
        type="button"
        onClick={() => onPatch(task.id, { status: done ? 'open' : 'done' })}
        disabled={busy}
        className={`h-4 w-4 rounded-sm border flex items-center justify-center ${
          done ? 'bg-emerald-500/40 border-emerald-500' : 'border-slate-600'
        }`}
        data-testid={`crm-task-toggle-${task.id}`}
        title={done ? 'Reopen' : 'Mark done'}
      >
        {done && <CheckSquare className="h-3 w-3 text-emerald-100" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${done ? 'line-through text-slate-500' : 'text-slate-100'}`}>
          {task.title}
        </div>
        <div className="text-[11px] text-slate-500">
          By {task.by} · {relTime(task.at)}
          {task.due_at && <> · due {new Date(task.due_at).toLocaleDateString()}</>}
          {task.completed_at && <> · done {relTime(task.completed_at)}</>}
        </div>
      </div>
    </div>
  );
}

export function TasksTab({ tasks, onAdd, onPatch, busy }) {
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');

  const submit = () => {
    if (!title.trim()) return;
    onAdd(title, due || null);
    setTitle('');
    setDue('');
  };

  const openTasks = tasks.filter((t) => t.status === 'open');
  const doneTasks = tasks.filter((t) => t.status !== 'open');

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Follow-up task…"
            className="flex-1 bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cn-primary/60"
            data-testid="crm-task-title"
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          <input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded-md px-2 py-1.5 text-sm text-slate-300"
            data-testid="crm-task-due"
          />
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={submit} disabled={busy || !title.trim()} data-testid="crm-add-task">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add task
          </Button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No tasks yet.</p>
      ) : (
        <>
          {openTasks.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                Open ({openTasks.length})
              </h4>
              {openTasks.map((t) => (
                <TaskRow key={t.id} task={t} onPatch={onPatch} busy={busy} />
              ))}
            </div>
          )}
          {doneTasks.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2 mt-4">
                Done ({doneTasks.length})
              </h4>
              {doneTasks.map((t) => (
                <TaskRow key={t.id} task={t} onPatch={onPatch} busy={busy} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TasksTab;
